import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useSuggestedArticle } from 'app/context/SuggestedArticleProvider';
import { useSetSuggestedArticleShown, useSetSuggestedArticleVisited } from 'app/hooks/mutate/usePatchSuggestedArticle';

import TailorSuggestedArticle from '../../components/TailorSuggestedArticle';
import useSuggestedArticleTracking from '../../hooks/useSuggestedArticleTracking';
import useHandleSearchResultClick from '../../hooks/useHandleSearchResultClick';

export default function ArticlePageSuggestedArticle({
  containerRef,
}: {
  containerRef: React.RefObject<HTMLDivElement>;
}) {
  const { mutate: setSuggestedArticleShown } = useSetSuggestedArticleShown();
  const { mutate: setSuggestedArticleVisited } = useSetSuggestedArticleVisited();

  const handleSearchResultClick = useHandleSearchResultClick();
  const { captureSuggestedTopicAppearance, captureSuggestedTopicClicked } = useSuggestedArticleTracking();
  const { suggestedArticle, isLoading, lastViewedArticle, suggestedArticleEnabled } = useSuggestedArticle();
  const [showSuggested, setShowSuggested] = useState(false);
  const handleScrollRef = useRef<() => void>();
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const handleScroll = () => {
    if (!containerRef.current) return;

    const scrollTop = containerRef.current.scrollTop;
    const scrollHeight = containerRef.current.scrollHeight - containerRef.current.clientHeight;
    const scrollPercent = (scrollTop / scrollHeight) * 100;

    if (scrollPercent >= 85 || scrollHeight === 0) {
      setSuggestedArticleShown();
      timeoutIdRef.current = setTimeout(
        () => {
          setShowSuggested(true);
        },
        scrollHeight === 0 ? 2000 : 0
      );
    }
  };

  const trackTopicAppearance = (el: HTMLDivElement | null) => {
    if (el) captureSuggestedTopicAppearance(suggestedArticle, lastViewedArticle);
  };

  useEffect(() => {
    if (!suggestedArticleEnabled || !suggestedArticle || showSuggested) return;

    handleScrollRef.current = handleScroll;

    const container = containerRef.current;
    if (container) {
      container.removeEventListener('scroll', handleScrollRef.current);
      container.addEventListener('scroll', handleScroll);
    }
    setTimeout(handleScroll, 2000);

    return () => {
      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
      }
    };
  }, [suggestedArticle, showSuggested, suggestedArticleEnabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleClickSuggestedArticle = () => {
    if (suggestedArticle) {
      setSuggestedArticleVisited();
      captureSuggestedTopicClicked(suggestedArticle, lastViewedArticle);
      handleSearchResultClick({
        result: suggestedArticle,
        resultIndex: 0,
        isSuggested: false,
        linkLocation: 'nbr',
      });
    }
  };

  if (!suggestedArticleEnabled || (!suggestedArticle && !isLoading)) return null;

  return (
    <div className="mt-6 sticky bottom-0 min-h-[100px]">
      {showSuggested && suggestedArticle && !isLoading && (
        <motion.div
          initial={{ y: 100 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          ref={trackTopicAppearance}
        >
          <TailorSuggestedArticle onClickSuggestedArticle={handleClickSuggestedArticle} />
        </motion.div>
      )}
    </div>
  );
}
